import { useEffect, useState } from 'react';
import { Box, Collapse, Switch } from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { SrOnlyText, TextBlock, TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { capitalizeText } from '../../../utils/helpers';
import { Customizations } from '../../../store/AccountCustomization';
import {
  GridPreferences,
  OrderRequestPreferences,
  WidgetPreferences,
  Widgets,
  getDefaultPreferences
} from '../../../store/User';
import { getProductsByFamily, ProductFamilies, useProductsStore } from '../../../store/Product';
import { DefaultInventoryGridColumnDefs } from '../../Pages/Inventory/inventoryGridColumnDefs';

type ColumnTypes = 'column' | 'widget' | 'order_request';

export interface DisableCustomizationsFormProps {
  type: ColumnTypes;
  header: string;
  subheader: string;
  gridPrefs?: GridPreferences;
  widgets?: Widgets;
  orderRequestPreferences?: OrderRequestPreferences;
  saveHandler: (customizations: Customizations) => void;
  className?: string;
}

const getRowItemStyle = (hidden: boolean) => {
  if (hidden) return 'hidden';
  return 'p-2 border-2 rounded my-4 flex flex-row justify-between items-center min-h-[40px] w-full shadow-md';
};

export const DisableCustomizationsForm = ({
  className,
  gridPrefs,
  widgets,
  orderRequestPreferences,
  header,
  subheader,
  type,
  saveHandler
}: DisableCustomizationsFormProps) => {
  const defaultPrefs = getDefaultPreferences();
  const [columnCustomizations, setColumnCustomizations] = useState<GridPreferences>(
    defaultPrefs.content.inventory_grid_All
  );
  const [widgetCustomizations, setWidgetCustomizations] = useState<Widgets>(defaultPrefs.content.widgets);
  const [expandedFamilies, setExpandedFamilies] = useState<Record<string, boolean>>({});
  const [orderRequestCustomizations, setOrderRequestCustomizations] = useState<OrderRequestPreferences>({
    disabledProductFamilies: [],
    disabledProducts: {},
    displayProductFamilyName: true,
    isAdvancedForm: false,
    displayLocationCode: false,
    advancedNewLocationSettings: false,
    quoteReviewApprovalWorkflow: false
  });
  const { fetchProducts } = useProductsStore();

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleProductToggle = (family: string, productId: string) => {
    const updated = { ...orderRequestCustomizations };
    updated.disabledProducts = updated.disabledProducts || {};
    const currentDisabled = updated.disabledProducts[family] || [];

    if (currentDisabled.includes(productId)) {
      updated.disabledProducts[family] = currentDisabled.filter((id) => id !== productId);
    } else {
      updated.disabledProducts[family] = [...currentDisabled, productId];
    }
    saveHandler(updated);
  };

  const toggleExpand = (family: string) => {
    setExpandedFamilies((prev) => ({ ...prev, [family]: !prev[family] }));
  };

  useEffect(() => {
    if (gridPrefs) {
      setColumnCustomizations({ ...gridPrefs });
    }

    if (widgets) {
      setWidgetCustomizations({ ...widgets });
    }

    if (orderRequestPreferences) {
      setOrderRequestCustomizations({ ...orderRequestPreferences });
    }
  }, [gridPrefs, widgets, orderRequestPreferences]);

  const booleanToggleMap: Record<string, keyof OrderRequestPreferences> = {
    advancedForm: 'isAdvancedForm',
    displayProductFamilyName: 'displayProductFamilyName',
    displayLocationCode: 'displayLocationCode',
    advancedNewLocationSettings: 'advancedNewLocationSettings',
    quoteReviewApprovalWorkflow: 'quoteReviewApprovalWorkflow'
  };

  const handleClick = (id: string) => {
    if (gridPrefs) {
      const updatedCustomizations = { ...columnCustomizations };
      if (updatedCustomizations.disabledFields.includes(id)) {
        updatedCustomizations.disabledFields = updatedCustomizations.disabledFields.filter((field) => field !== id);
      } else {
        updatedCustomizations.disabledFields = [...updatedCustomizations.disabledFields, id];
      }
      saveHandler(updatedCustomizations);
    }

    if (widgets) {
      const updatedCustomizations = { ...widgetCustomizations };
      Object.keys(updatedCustomizations).forEach((widget) => {
        const matchingCol = findMatchingCol(id, updatedCustomizations[widget as keyof Widgets]);
        if (matchingCol) matchingCol.disabled = !matchingCol.disabled;
      });
      saveHandler(updatedCustomizations);
    }

    if (type === 'order_request') {
      const toggleKey = booleanToggleMap[id];
      if (toggleKey) {
        saveHandler({ ...orderRequestCustomizations, [toggleKey]: !orderRequestCustomizations[toggleKey] });
      } else {
        const updatedCustomizations = { ...orderRequestCustomizations };
        if (updatedCustomizations.disabledProductFamilies?.includes(id)) {
          updatedCustomizations.disabledProductFamilies = updatedCustomizations.disabledProductFamilies.filter(
            (family) => family !== id
          );
        } else {
          updatedCustomizations.disabledProductFamilies = [
            ...(updatedCustomizations.disabledProductFamilies || []),
            id
          ];
        }
        saveHandler(updatedCustomizations);
      }
    }
  };

  const findMatchingCol = (id: string, customizations: WidgetPreferences[]) => {
    return customizations.find((col) => col.widgetId === id);
  };

  const getAllWidgets = () => {
    return Object.entries(widgetCustomizations).reduce((acc: WidgetPreferences[], [key, value]) => {
      if (value && Array.isArray(value) && value.length > 0) {
        value.forEach((widget) => {
          if (widget.widgetId && !acc.some((w) => w.widgetId === widget.widgetId)) {
            acc.push({
              ...widget
            });
          }
        });
      }
      return acc;
    }, []);
  };

  return (
    <div className={className}>
      <div className="flex flex-col items-center">
        <div className="py-4 border-b-2 w-full">
          <TextBlock
            color="black"
            size="xxl">
            {header}
          </TextBlock>
        </div>
        <div className="py-6 w-full">
          <TextBlock
            color="black"
            size="base">
            {subheader}
          </TextBlock>
        </div>
        {type !== 'order_request' && (
          <>
            <div className="pb-4 border-b-2 w-full">
              <TextBlock
                color="black"
                size="lg">
                {capitalizeText(type)}s
              </TextBlock>
            </div>
            <div className="relative pb-4 border-b-2 box-border mt-4 mb-2 max-h-[40vh] w-full overflow-auto">
              {gridPrefs && (
                <div data-cy="edit-column-list-container">
                  {DefaultInventoryGridColumnDefs.map((col) => {
                    return (
                      <div
                        key={col.field}
                        className={getRowItemStyle(col.field === 'details')}>
                        <TextSpan
                          className="px-2"
                          dataTestId={col.headerName}>
                          {col.headerName}
                        </TextSpan>
                        <button
                          data-testid={`${col.field}-visibility-button`}
                          data-cy={`${col.field}-visibility-button`}
                          onClick={() => handleClick(col.field)}
                          className="px-3 py-1 border border-grey-3 box-border rounded flex flex-row justify-between items-center w-30">
                          <Icon
                            type={
                              columnCustomizations.disabledFields.includes(col.field || '') ? 'disabled' : 'enabled'
                            }
                          />
                          <SrOnlyText>{`${columnCustomizations.disabledFields.includes(col.field || '') ? 'Enable' : 'Disable'} ${col.field} column`}</SrOnlyText>
                          <TextSpan
                            className="ml-1"
                            size="sm"
                            weight="bold">
                            {columnCustomizations.disabledFields.includes(col.field || '') ? 'Disabled' : 'Enabled'}
                          </TextSpan>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {widgets && (
                <>
                  {getAllWidgets().map((cust) => {
                    return (
                      <div
                        key={cust.widgetId}
                        className={getRowItemStyle(false)}>
                        <TextSpan className="px-2">{cust.headerName}</TextSpan>
                        <button
                          onClick={() => handleClick(cust.widgetId)}
                          className="px-3 py-1 border border-grey-3 box-border rounded flex flex-row justify-between items-center w-30">
                          <Icon type={cust.disabled ? 'disabled' : 'enabled'} />
                          <SrOnlyText>{`${cust.disabled ? 'Enable' : 'Disable'} ${cust.headerName} column`}</SrOnlyText>
                          <TextSpan
                            className="ml-1"
                            size="sm"
                            weight="bold">
                            {columnCustomizations.disabledFields.includes(cust.headerName) ? 'Disabled' : 'Enabled'}
                          </TextSpan>
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
        {type === 'order_request' && (
          <>
            <div className="pb-4 border-b-2 w-full">
              <TextBlock
                color="black"
                size="lg">
                Product Families
              </TextBlock>
            </div>
            <div className="px-2 relative mt-4 mb-2 max-h-[60vh] w-full overflow-auto">
              {ProductFamilies.map((family) => {
                const isFamilyDisabled = orderRequestCustomizations.disabledProductFamilies?.includes(family);
                const familyProducts = getProductsByFamily(family);

                return (
                  <Box
                    key={family}
                    sx={{
                      mb: 2,
                      borderRadius: 1,
                      bgcolor: expandedFamilies[family] ? '#f5f5f5' : 'transparent',
                      p: expandedFamilies[family] ? 1 : 0
                    }}>
                    <div className="p-2 border-2 rounded flex flex-row justify-between items-center min-h-[40px] w-full shadow-md bg-white">
                      <button
                        aria-expanded={!!expandedFamilies[family]}
                        aria-label={`${expandedFamilies[family] ? 'Collapse' : 'Expand'} ${family}`}
                        className="flex items-center cursor-pointer"
                        onClick={() => toggleExpand(family)}
                        type="button">
                        {expandedFamilies[family] ? <ExpandMore /> : <ChevronRight />}
                        <TextSpan className="ml-2 font-bold">{family}</TextSpan>
                      </button>
                      <button
                        onClick={() => handleClick(family)}
                        className="px-3 py-1 border border-grey-3 rounded flex flex-row items-center">
                        <Icon type={isFamilyDisabled ? 'disabled' : 'enabled'} />
                        <TextSpan
                          className="ml-1"
                          size="sm"
                          weight="bold">
                          {isFamilyDisabled ? 'Disabled' : 'Enabled'}
                        </TextSpan>
                      </button>
                    </div>

                    <Collapse
                      in={expandedFamilies[family]}
                      timeout="auto"
                      unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        {familyProducts.map((product) => {
                          const isProductDisabled =
                            isFamilyDisabled ||
                            (orderRequestCustomizations.disabledProducts || {})[family]?.includes(product.id);
                          return (
                            <div
                              key={product.id}
                              className="p-2 mb-3 border-2 rounded my-2 flex flex-row justify-between items-center min-h-[40px] bg-[#E5EDFF] w-full shadow-md">
                              <TextSpan className="ml-2 font-bold">{product.name}</TextSpan>
                              <button
                                className={`px-3 py-1 border border-grey-3 rounded flex flex-row items-center ${isFamilyDisabled ? 'opacity-50' : ''}`}
                                disabled={isFamilyDisabled}
                                onClick={() => handleProductToggle(family, product.id)}>
                                <Icon type={isProductDisabled ? 'disabled' : 'enabled'} />
                                <TextSpan
                                  className="ml-1"
                                  size="sm"
                                  weight="bold">
                                  {isProductDisabled ? 'Disabled' : 'Enabled'}
                                </TextSpan>
                              </button>
                            </div>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </div>
            <div className="pb-4 border-b-2 w-full">
              <TextBlock
                color="black"
                size="lg">
                Display Settings
              </TextBlock>
            </div>
            <div className="px-2 my-4 flex flex-row justify-between items-center min-h-[40px] w-full">
              <TextSpan className="max-w-[400px]">
                Display Product Families, off just displays the product itself ie: Network - Broadband vs Broadband
              </TextSpan>
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={orderRequestCustomizations.displayProductFamilyName === true}
                onClick={() => handleClick('displayProductFamilyName')}
              />
            </div>
            <div className="px-2 my-4 flex flex-row justify-between items-center min-h-[40px] w-full">
              <TextSpan className="max-w-[400px]">
                Display Location Code instead of the full address for &quot;Existing Inventory&quot; or
                &quot;Location&quot; selection fields
              </TextSpan>
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={orderRequestCustomizations.displayLocationCode === true}
                onClick={() => handleClick('displayLocationCode')}
                inputProps={{ 'aria-label': 'Display Location Code' }}
              />
            </div>
            <div className="px-2 my-4 flex flex-row justify-between items-center min-h-[40px] w-full">
              <TextSpan className="max-w-[400px]">
                Display Location Code, Name and Floor, Suite and Room fields when the user selects &quot;New
                Location&quot;
              </TextSpan>
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={orderRequestCustomizations.advancedNewLocationSettings === true}
                onClick={() => handleClick('advancedNewLocationSettings')}
                inputProps={{ 'aria-label': 'Advanced Settings: New Locations' }}
              />
            </div>
            <div className="px-2 my-4 flex flex-row justify-between items-center min-h-[40px] w-full">
              <TextSpan className="max-w-[400px]">Display Quote Details Review and Approval Workflow</TextSpan>
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={orderRequestCustomizations.quoteReviewApprovalWorkflow === true}
                onClick={() => handleClick('quoteReviewApprovalWorkflow')}
                inputProps={{ 'aria-label': 'Quote Review and Approval Workflow' }}
              />
            </div>
            <div className="pb-4 border-b-2 w-full">
              <TextBlock
                color="black"
                size="lg">
                Form Version
              </TextBlock>
            </div>
            <div className="px-2 my-4 flex flex-row justify-between items-center min-h-[40px] w-full">
              <TextSpan>Configure access to product family specific data capture?</TextSpan>
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={orderRequestCustomizations.isAdvancedForm === true}
                onClick={() => handleClick('advancedForm')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
